import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PhotoUploadModal = ({ 
  isOpen = false, 
  onClose, 
  onUpload, 
  orderInfo = null 
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event?.target?.files);
    setSelectedFiles(files);
    
    // Create preview URLs
    const urls = files?.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleCameraCapture = () => {
    if (fileInputRef?.current) {
      fileInputRef?.current?.setAttribute('capture', 'environment');
      fileInputRef?.current?.click();
    }
  };

  const handleRemovePhoto = (index) => {
    const newFiles = selectedFiles?.filter((_, i) => i !== index);
    const newUrls = previewUrls?.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(previewUrls?.[index]);
    
    setSelectedFiles(newFiles);
    setPreviewUrls(newUrls);
  };

  const handleUpload = async () => {
    if (selectedFiles?.length === 0) return;
    
    setIsUploading(true);
    
    try {
      const uploadData = {
        files: selectedFiles,
        description,
        orderId: orderInfo?.id,
        timestamp: new Date()?.toISOString(),
        location: await getCurrentLocation()
      };
      
      await onUpload(uploadData);
      handleClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          () => resolve(null)
        );
      } else {
        resolve(null);
      }
    });
  };

  const handleClose = () => {
    // Clean up preview URLs
    previewUrls?.forEach(url => URL.revokeObjectURL(url));
    
    setSelectedFiles([]);
    setPreviewUrls([]);
    setDescription('');
    setIsUploading(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1400] bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="fixed inset-x-4 top-1/2 transform -translate-y-1/2 max-w-md mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-modal overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-card-foreground">
              Adicionar Fotos
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isUploading}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            {/* Order Info */}
            {orderInfo && (
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm font-medium text-card-foreground">
                  OS #{orderInfo?.id}
                </p>
                <p className="text-xs text-muted-foreground">
                  {orderInfo?.clientName} - {orderInfo?.serviceType}
                </p>
              </div>
            )}

            {/* Upload Options */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleCameraCapture}
                className="touch-target flex-col h-auto py-4"
                disabled={isUploading}
              >
                <Icon name="Camera" size={24} className="mb-2" />
                <span className="text-sm">Câmera</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => fileInputRef?.current?.click()}
                className="touch-target flex-col h-auto py-4"
                disabled={isUploading}
              >
                <Icon name="Image" size={24} className="mb-2" />
                <span className="text-sm">Galeria</span>
              </Button>
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Photo Previews */}
            {previewUrls?.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-card-foreground">
                  Fotos Selecionadas ({selectedFiles?.length})
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {previewUrls?.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-border"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isUploading}
                      >
                        <Icon name="X" size={12} />
                      </Button>
                      <div className="absolute bottom-1 left-1 bg-background/80 rounded px-1">
                        <span className="text-xs text-foreground">
                          {formatFileSize(selectedFiles?.[index]?.size || 0)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <Input
              label="Descrição (opcional)"
              type="text"
              placeholder="Descreva o que está sendo fotografado..."
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              disabled={isUploading}
            />

            {/* Upload Progress */}
            {isUploading && (
              <div className="bg-muted rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-card-foreground">
                    Enviando fotos...
                  </span>
                </div>
                <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse"></div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancelar
            </Button>
            
            <Button
              variant="default"
              onClick={handleUpload}
              disabled={selectedFiles?.length === 0 || isUploading}
              loading={isUploading}
              iconName="Upload"
              iconPosition="left"
            >
              {isUploading ? 'Enviando...' : `Enviar ${selectedFiles?.length} foto${selectedFiles?.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;